import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/i18n/LocaleContext';

export const SOCIAL_LINKS = {
  telegram: 'https://t.me/portalpeipeibsc',
  x: 'https://x.com/peipei_XAUt',
};

export const TelegramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M21.94 4.35a1.5 1.5 0 0 0-2.05-1.7L2.9 9.4c-1.35.53-1.26 2.47.13 2.88l4.32 1.27 1.6 5.06c.4 1.28 2.03 1.6 2.9.58l1.87-2.2 4.02 2.95c1.02.75 2.47.19 2.7-1.06l2.5-14.53Zm-3.98 2.2L9.7 13.6a.75.75 0 0 0-.24.4l-.53 2.6-1.03-3.27 9.03-6.9c.35-.27.75.3.43.62l-.4.5Z" />
  </svg>
);

export const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.21-6.82-5.97 6.82H1.67l7.73-8.84L1.25 2.25h6.83l4.71 6.23 5.45-6.23Zm-1.16 17.52h1.83L7.08 4.13H5.12l11.96 15.64Z" />
  </svg>
);

// Compact icon links for the header.
export const SocialIconLinks = () => {
  const { t } = useLocale();
  return (
    <div className="flex items-center gap-1.5">
      <a
        href={SOCIAL_LINKS.telegram}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="header-social-telegram"
        aria-label={t.social.telegramLabel}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-secondary text-muted-foreground hover:text-primary hover:border-primary/60"
      >
        <TelegramIcon className="h-4 w-4" />
      </a>
      <a
        href={SOCIAL_LINKS.x}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="header-social-x"
        aria-label={t.social.xLabel}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-secondary text-muted-foreground hover:text-primary hover:border-primary/60"
      >
        <XIcon className="h-4 w-4" />
      </a>
    </div>
  );
};

// Yellow lantern band above the footer with the community links.
export const CommunityBand = () => {
  const { t } = useLocale();
  const reduced = useReducedMotion();

  return (
    <section data-testid="community-section" className="relative border-t-4 border-ink bg-primary overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{ backgroundImage: 'url(/assets/04-backgrounds/lantern-chain-pattern.svg)', backgroundSize: '380px' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid lg:grid-cols-[auto_1fr] items-center gap-10 lg:gap-16">
          <motion.img
            src="/assets/01-mascot/cutouts/peipei-lantern-flat.png"
            alt=""
            aria-hidden="true"
            initial={reduced ? {} : { opacity: 0, y: 24, rotate: -4 }}
            whileInView={reduced ? {} : { opacity: 1, y: 0, rotate: -2 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-40 sm:w-52 mx-auto lg:mx-0 drop-shadow-xl"
            loading="lazy"
          />
          <motion.div
            initial={reduced ? {} : { opacity: 0, y: 24 }}
            whileInView={reduced ? {} : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="text-center lg:text-left"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-snug text-primary-foreground">
              {t.social.title}
            </h2>
            <p className="mt-3 text-base md:text-lg leading-relaxed text-primary-foreground/80 max-w-xl mx-auto lg:mx-0">
              {t.social.body}
            </p>
            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4">
              <Button
                asChild
                size="lg"
                data-testid="community-telegram-cta"
                className="h-12 px-7 text-base font-bold bg-ink text-primary hover:bg-ink/85 border-2 border-ink shadow-[4px_4px_0_hsl(var(--accent))] hover:shadow-[2px_2px_0_hsl(var(--accent))] gap-2"
              >
                <a href={SOCIAL_LINKS.telegram} target="_blank" rel="noopener noreferrer">
                  <TelegramIcon className="h-5 w-5" />
                  {t.social.telegramCta}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="community-x-cta"
                className="h-12 px-7 text-base font-bold bg-transparent text-primary-foreground border-2 border-ink hover:bg-ink hover:text-primary gap-2"
              >
                <a href={SOCIAL_LINKS.x} target="_blank" rel="noopener noreferrer">
                  <XIcon className="h-5 w-5" />
                  {t.social.xCta}
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
